import type {Hotel} from '@/modules/hotels/components/MapView/models';
import type {AsyncStatus} from '@react-hookz/web';
import type {ComponentType} from 'react';

import {KeywordTypes} from '@/api/keywordApi';
import {useDestination} from '@/modules/common/hooks/useDestination';
import {fromKeywordToGiata} from '@/modules/common/utils/hotelIdFormat';
import {NoResultsMessage} from '@/modules/hotels/components/MapView/components/NoResultsMessage';
import {MapComponent} from '@/modules/hotels/components/MapView/MapComponent';
import {useAppSelector} from '@/redux/config';
import {
  getHotelCountFiltered,
  getHotelCountUnfiltered,
  selectAllHotels,
  selectAreHotelsLoading,
} from '@/redux/v1/hotels/list/reducer';

type Props = {
  mode: 'overlay' | 'sticky';
  canShowNoResultsNotification: boolean;
  tabRowEnabled?: boolean;
  travellerCount: number;
  onClose: () => void;
  onFilterReset: () => void;
  filters: React.ReactNode;
  searchNode?: React.ReactNode;
  mapTrigger?: React.ReactNode;
  alternativeLayout?: boolean;
  splitView?: boolean;
  promotedHotel: Hotel | null;
  promotedHotelId: string;
  promotedHotelEnabled: boolean;
  promotedHotelStatus: AsyncStatus;
};

export const MapView: ComponentType<Props> = ({
  mode,
  canShowNoResultsNotification,
  tabRowEnabled,
  travellerCount,
  onClose,
  onFilterReset,
  filters,
  searchNode,
  mapTrigger,
  alternativeLayout,
  splitView,
  promotedHotel,
  promotedHotelId,
  promotedHotelEnabled,
  promotedHotelStatus,
}: Props) => {
  const destination = useDestination();

  const allHotels = useAppSelector(selectAllHotels);
  const areHotelsLoading = useAppSelector(selectAreHotelsLoading);
  const hotelCountUnfiltered = useAppSelector(getHotelCountUnfiltered);
  const hotelCountFiltered = useAppSelector(getHotelCountFiltered);

  // @TODO needs to account for loading state
  const noResultsNotificationVisible =
    canShowNoResultsNotification &&
    (hotelCountUnfiltered === 0 || hotelCountFiltered === 0);

  let initialSelectedHotelId = '';
  if (destination.type === KeywordTypes.hotel) {
    initialSelectedHotelId = fromKeywordToGiata(destination.id);
  } else if (allHotels.length > 0) {
    initialSelectedHotelId = allHotels[0].id;
  }

  return (
    <MapComponent
      hotels={allHotels}
      promotedHotel={promotedHotel}
      promotedHotelId={promotedHotelId}
      promotedHotelEnabled={promotedHotelEnabled}
      promotedHotelStatus={promotedHotelStatus}
      filters={filters}
      searchNode={searchNode}
      mapTrigger={mapTrigger}
      alternativeLayout={alternativeLayout}
      splitView={splitView}
      initialSelectedHotelId={initialSelectedHotelId}
      isLoading={areHotelsLoading}
      canHaveLoadingOverlay={mode === 'sticky'}
      notification={
        canShowNoResultsNotification && (
          <NoResultsMessage onClickFilterResetButton={onFilterReset} />
        )
      }
      isNotificationVisible={noResultsNotificationVisible}
      tabRowEnabled={tabRowEnabled}
      travellerCount={travellerCount}
      onClose={onClose}
    />
  );
};
